import {
    Component,
    EventEmitter,
    Input,
    Output,
    SimpleChanges,
    ViewChild,
    ChangeDetectorRef,
} from '@angular/core';
import moment from 'moment';
import { OverlayPanel } from 'primeng/overlaypanel';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { PrimeNGConfig } from 'primeng/api';
import { ToastMessageService } from 'src/app/services/toast-message/toast-message.service';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

/*************  data model *****************/
export interface DateRange {
    start: Date | null;
    end: Date | null;
};

export type PresetKey =
    | 'TODAY'
    | 'YESTERDAY'
    | 'THIS_WEEK'
    | 'LAST_WEEK'
    | 'THIS_MONTH'
    | 'LAST_MONTH'
    | 'THIS_YEAR'
    | 'LAST_YEAR'
    | 'CUSTOM';

/*************  Preset Factory *****************/
const factory: Record<PresetKey, () => DateRange> = {
    TODAY: () => {
        const m = moment().startOf('day');
        return { start: m.toDate(), end: m.endOf('day').toDate() };
    },
    YESTERDAY: () => {
        const m = moment().subtract(1, 'day').startOf('day');
        return { start: m.toDate(), end: m.endOf('day').toDate() };
    },
    THIS_WEEK: () => ({
        start: moment().startOf('weeks').toDate(),
        end: moment().endOf('weeks').toDate(),
    }),
    LAST_WEEK: () => ({
        start: moment().subtract(1, 'weeks').startOf('weeks').toDate(),
        end: moment().subtract(1, 'weeks').endOf('weeks').toDate(),
    }),
    THIS_MONTH: () => ({
        start: moment().startOf('month').toDate(),
        end: moment().endOf('month').toDate(),
    }),
    LAST_MONTH: () => ({
        start: moment().subtract(1, 'month').startOf('month').toDate(),
        end: moment().subtract(1, 'month').endOf('month').toDate(),
    }),
    THIS_YEAR: () => ({
        start: moment().startOf('year').toDate(),
        end: moment().endOf('year').toDate(),
    }),
    LAST_YEAR: () => ({
        start: moment().subtract(1, 'year').startOf('year').toDate(),
        end: moment().subtract(1, 'year').endOf('year').toDate(),
    }),
    CUSTOM: () => ({ start: null, end: null }),
};

@Component({
    selector: 'app-calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss'],
})

export class CalendarComponent {
    /*************  Inputs & Outputs *****************/
    @Input() preset: PresetKey = 'THIS_YEAR';
    @Input() returnFmt: string = 'YYYY-MM-DD';
    @Input() startDate?: string | null; // Expecting 'YYYY-MM-DD'
    @Input() endDate?: string | null; // Expecting 'YYYY-MM-DD'
    @Output() apply = new EventEmitter<DateRange | { start: string; end: string }>();

    /*************  Template Refs *******************/
    @ViewChild('overlay') overlay!: OverlayPanel;

    /*************  State ***************************/
    selectedPreset: PresetKey = 'THIS_YEAR';
    range: DateRange = factory[this.preset]();
    calendarModel: Date[] = [];
    locale: 'vi' | 'en' = 'vi';
    overlayVisible = false;

    private backupPreset!: PresetKey;
    private backupRange!: DateRange;
    private backupCalendar!: Date[];
    readonly presetKeys: PresetKey[] = [
        'TODAY',
        'YESTERDAY',
        'THIS_WEEK',
        'LAST_WEEK',
        'THIS_MONTH',
        'LAST_MONTH',
        'THIS_YEAR',
        'LAST_YEAR',
        'CUSTOM',
    ];
    
    form: FormGroup;
    private langSub?: Subscription;

    /*************  constructor ***************************/
    constructor(
        private fb: FormBuilder,
        private translate: TranslateService,
        private toastMessageService: ToastMessageService,
        private primengCfg: PrimeNGConfig,
        private cd: ChangeDetectorRef
    ) {
        this.form = this.fb.group({
            start: ['', [Validators.required, this.dateValidator.bind(this)]],
            end: ['', [Validators.required, this.dateValidator.bind(this)]],
        }, { updateOn: 'blur' });
    }

    /*************  Lifecycle ***********************/
    ngOnInit(): void {
        this.locale = (this.translate.currentLang as 'vi' | 'en') || 'en';
        this.setupPrimeNgLocale();
        if (!this.startDate && !this.endDate) {
            this.setPreset(this.preset);
        }
        this.form.valueChanges.subscribe(() => this.syncRangeFromForm());
        this.langSub = this.translate.onLangChange.subscribe((e: LangChangeEvent) => {
            this.locale = (e.lang as 'vi' | 'en') || 'en';
            this.setupPrimeNgLocale();
            this.patchForm();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['startDate'] || changes['endDate']) {
            this.locale = (this.translate.currentLang as 'vi' | 'en') || 'en';
            this.setupPrimeNgLocale();
            this.updateFromInputs();
        }
    }

    ngOnDestroy(): void {
        this.langSub?.unsubscribe();
    }

    /*************  UI helpers **********************/
    get displayValue(): string {
        const fmt = this.locale === 'vi' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        const { start, end } = this.range;
        if (!start && !end) {
            return ''
        }
        if (start && end && moment(start).isSame(end, 'day')) {
            return moment(start).format(fmt);
        }
        return `${moment(start).format(fmt)} - ${moment(end ?? new Date()).format(fmt)}`;
    }

    get fc() {
        return this.form.controls as { start: AbstractControl; end: AbstractControl };
    }

    /*************  Preset logic ********************/
    setPreset(key: PresetKey): void {
        this.selectedPreset = key;
        this.range = factory[key]();
        this.calendarModel = this.range.start
          ? this.range.end
            ? [this.range.start, this.range.end]
            : [this.range.start]
          : [];
        this.form.markAsUntouched({ onlySelf: true });
        this.form.markAsPristine({ onlySelf: true });
        this.patchForm();
    }

    /*************  Calendar events *****************/
    onCalendarChange(ev: Date[]): void {
        if (!ev || ev.length === 0) return;
        this.range = { start: ev[0] ?? null, end: ev[1] ?? null };
        this.selectedPreset = 'CUSTOM';
        this.patchForm();
    }

    /*************  Buttons *************************/
    openPanel(event: any) {
        this.backupPreset = this.selectedPreset;
        this.backupRange = { ...this.range };
        this.backupCalendar = [...this.calendarModel];
        this.overlay.show(event);
        this.overlayVisible = true;
    }

    onCancel() {
        this.selectedPreset = this.backupPreset;
        this.range = { ...this.backupRange };
        this.calendarModel = [...this.backupCalendar];
        this.patchForm();
        this.overlay.hide();
        this.overlayVisible = false;
    }

    onApply(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) return;
        const { start, end } = this.range;
        if (start && end && moment(end).isBefore(start, 'day')) {
          this.toastMessageService.showError(
            this.translate.instant('CALENDAR_FILTER.START_DATE_GREATE_END_DATE')
          );
          return;
        }
        this.apply.emit(this.serialize());
        this.backupPreset   = this.selectedPreset;
        this.backupRange    = { ...this.range };
        this.backupCalendar = [...this.calendarModel];
        this.overlay.hide();
        this.overlayVisible = false;
    }

    /*************  Helpers *************************/
    serialize() {
        if (this.returnFmt === 'date') return this.range;
        const s = this.range.start
            ? moment(this.range.start).format(this.returnFmt)
            : '';
        const e = this.range.end
            ? moment(this.range.end).format(this.returnFmt)
            : '';
    
        return { start: s, end: e };
      };

    patchForm(): void {
        const fmt = this.locale === 'vi' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        const { start, end } = this.range;
        this.form.patchValue(
            {
              start: start ? moment(start).format(fmt) : '',
              end: end ? moment(end).format(fmt) : '',
            },
            { emitEvent: false }
        );
    }
  
    updateFromInputs(): void {
        const ISO_FMT = 'YYYY-MM-DD';
      
        const mStart = this.startDate ? moment(this.startDate, ISO_FMT, true) : null;
        const mEnd   = this.endDate   ? moment(this.endDate,   ISO_FMT, true) : null;

        if (!mStart?.isValid() || !mEnd?.isValid()) return;
      
        this.range = { start: mStart.toDate(), end: mEnd.toDate() };
        this.calendarModel = [this.range.start, this.range.end].filter((d): d is Date => d instanceof Date);
      
        const weekStartsMonday = this.locale === 'vi';
        const weekStart = (d: moment.Moment): moment.Moment => weekStartsMonday ? d.clone().startOf('week') : d.clone().startOf('isoWeek');
        const weekEnd = (d: moment.Moment): moment.Moment => weekStart(d).clone().add(6, 'days');
      
        const today = moment();
        const thisWeekStart = weekStart(today);
        const thisWeekEnd = weekEnd(today);
        const lastWeekStart = weekStart(today.clone().subtract(1, 'week'));
        const lastWeekEnd = weekEnd(today.clone().subtract(1, 'week'));

        const thisMonthStart = today.clone().startOf('month');
        const thisMonthEnd = today.clone().endOf('month');
        const lastMonthStart = today.clone().subtract(1, 'month').startOf('month');
        const lastMonthEnd = today.clone().subtract(1, 'month').endOf('month');
      
        const thisYearStart = today.clone().startOf('year');
        const thisYearEnd = today.clone().endOf('year');
        const lastYearStart = today.clone().subtract(1, 'year').startOf('year');
        const lastYearEnd = today.clone().subtract(1, 'year').endOf('year');

        let preset: PresetKey = 'CUSTOM';
      
        if (mStart.isSame(today, 'day') && mEnd.isSame(today, 'day')) {
            preset = 'TODAY';
        } else if (
            mStart.isSame(today.clone().subtract(1, 'day'), 'day') &&
            mEnd.isSame(mStart, 'day')
        ) {
            preset = 'YESTERDAY';
        } else if (
            mStart.isSame(thisWeekStart, 'day') && mEnd.isSame(thisWeekEnd, 'day')
        ) {
            preset = 'THIS_WEEK';
        } else if (
            mStart.isSame(lastWeekStart, 'day') && mEnd.isSame(lastWeekEnd, 'day')
        ) {
            preset = 'LAST_WEEK';
        } else if (
            mStart.isSame(thisMonthStart, 'day') && mEnd.isSame(thisMonthEnd, 'day')
        ) {
            preset = 'THIS_MONTH';
        } else if (
            mStart.isSame(lastMonthStart, 'day') && mEnd.isSame(lastMonthEnd, 'day')
        ) {
            preset = 'LAST_MONTH';
        } else if (
            mStart.isSame(thisYearStart, 'day') && mEnd.isSame(thisYearEnd, 'day')
        ) {
            preset = 'THIS_YEAR';
        } else if (
            mStart.isSame(lastYearStart, 'day') && mEnd.isSame(lastYearEnd, 'day')
        ) {
            preset = 'LAST_YEAR';
        }

        this.selectedPreset = preset;

        this.patchForm();
        this.cd.markForCheck();
      }

    syncRangeFromForm() {
        const fmt = this.locale === 'vi' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        const { start, end } = this.form.value as { start: string; end: string };
    
        const mStart = moment(start, fmt, true);
        const mEnd = moment(end, fmt, true);
    
        if (!mStart.isValid()) return;
    
        this.range.start = mStart.toDate();
        this.range.end = mEnd.isValid() ? mEnd.toDate() : null;
    
        this.calendarModel = this.range.end
            ? [this.range.start, this.range.end]
            : [this.range.start];
    
        this.selectedPreset = 'CUSTOM';
    }

    private setupPrimeNgLocale() {
        moment.locale(this.locale);
        this.primengCfg.setTranslation({
            dayNames: this.translate.instant('PRIMENG.DAY_NAMES'),
            dayNamesShort: this.translate.instant('PRIMENG.DAY_NAMES_SHORT'),
            dayNamesMin: this.translate.instant('PRIMENG.DAY_NAMES_MIN'),
            monthNames: this.translate.instant('PRIMENG.MONTH_NAMES'),
            monthNamesShort: this.translate.instant('PRIMENG.MONTH_NAMES_SHORT'),
            today: this.translate.instant('PRIMENG.TODAY'),
            weekHeader: this.translate.instant('PRIMENG.WEEK_HEADER'),
        });
    }

    /** Compare helper */
    isSame(dateObj: any, target: Date | null): boolean {
        if (!target) return false;
        const d = moment(target);
        return (
            d.date() === dateObj.day &&
            d.month() === dateObj.month &&
            d.year() === dateObj.year
        );
    }

    /** Public methods used in template */
    isStartDate(date: any): boolean {
        return this.isSame(date, this.range.start);
    }

    isEndDate(date: any): boolean {
        return this.isSame(date, this.range.end);
    }

    isInRange(date: any): boolean {
        const { start, end } = this.range;
        if (!start || !end) return false;
            const current = moment({
            year: date.year,
            month: date.month,
            day: date.day,
        });
        return current.isAfter(moment(start), 'day') && current.isBefore(moment(end), 'day');
    }

    /*************  Validators **********************/
    private dateValidator(control: any) {
        const value: string = control.value;
        if (!value) return null;
        const fmt = this.locale === 'vi' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        return moment(value, fmt, true).isValid() ? null : { format: true };
    }
}
